/**
 * Strategies for distributing an inbound or transferred call across
 * a multi-rep destination (Group / RbacRole). The choice trades off
 * speed-of-answer against load-balancing and per-rep fairness.
 *
 *   - **simultaneous** — ring every available member at once; the
 *     first to answer wins. Best for "everyone available, get the
 *     customer to ANYONE fast" (small teams, high-priority lines).
 *
 *   - **round-robin** — pick the next member in a rotating order;
 *     ring only them. If they don't answer within the strategy's
 *     ring timeout, advance to the next one. Even distribution by
 *     position, not by load.
 *
 *   - **longest-idle** — pick the member who has been off the phone
 *     the longest (lastCallCompletedAt ASC, NULLS FIRST so reps
 *     who have never received a call get priority). Best load-
 *     balancing semantics; requires presence tracking on each rep
 *     (`SoftphoneNumberConfigEntity.lastCallCompletedAt`).
 *
 * The DND filter applies to all three: members with `isDndEnabled = true`
 * are excluded before the strategy runs. If the post-DND set is empty
 * the call falls through to voicemail (consistent with the existing
 * single-rep DND behavior in `resolveInbound`).
 */
export const SOFTPHONE_RING_STRATEGIES = [
	'simultaneous',
	'round-robin',
	'longest-idle',
] as const;

export type SoftphoneRingStrategyModel =
	(typeof SOFTPHONE_RING_STRATEGIES)[number];

/**
 * Hard-coded fallback used when neither the per-Group / per-Role
 * config nor the tenant default is set. Simultaneous is the safest
 * default — it ensures coverage (any available rep can answer) at
 * the cost of fairness (eager-answer reps get more calls). Tenants
 * can override per-destination once they've established preferences.
 */
export const SOFTPHONE_DEFAULT_RING_STRATEGY: SoftphoneRingStrategyModel =
	'simultaneous';

/**
 * `InstanceSettingEntity.name` key for the tenant-wide default ring
 * strategy. Stored as JSON: `{ ringStrategy: SoftphoneRingStrategyModel }`.
 * Read by `SoftphoneRingStrategyResolverService` as the second tier in
 * the resolution chain (per-destination → tenant default → hard-coded).
 */
export const SOFTPHONE_TENANT_DEFAULT_RING_STRATEGY_SETTING_KEY =
	'softphone.defaultRingStrategy';

export interface SoftphoneTenantDefaultRingStrategyValueModel {
	ringStrategy: SoftphoneRingStrategyModel;
}

/**
 * `InstanceSettingEntity.name` key for the tenant-wide default hold
 * music URL (DD.4.E). Stored as JSON: `{ holdMusicUrl: string }`.
 * Per-rep override on `SoftphoneNumberConfigEntity.holdMusicUrl`
 * wins; absent that, tenant default applies; absent that, the
 * hard-coded Twilio Borghestral fallback.
 */
export const SOFTPHONE_TENANT_DEFAULT_HOLD_MUSIC_SETTING_KEY =
	'softphone.defaultHoldMusicUrl';

export interface SoftphoneTenantDefaultHoldMusicValueModel {
	holdMusicUrl: string;
}

/**
 * `InstanceSettingEntity.name` key for the tenant-wide recording
 * default (DD.11.1). Stored as JSON: `{ recordingEnabled: boolean }`.
 *
 * Hierarchy: per-number `softphone_number.recordingEnabled` always
 * wins. The tenant default is consulted ONLY at number-assignment
 * time as the seed value for a freshly-assigned number — admins
 * can then override per-line. Once set, the per-number value
 * sticks; flipping the tenant default later does NOT retroactively
 * change existing assignments (compliance: tenants may want some
 * lines never recorded regardless of tenant policy).
 *
 * Recording-on implies transcription + AI summary (DD.11.2 ships
 * those automatically as part of the post-call processing pipeline
 * because Deepgram + LLM costs are negligible). One toggle, three
 * outputs: audio file, transcript, summary.
 */
export const SOFTPHONE_TENANT_DEFAULT_RECORDING_SETTING_KEY =
	'softphone.defaultRecordingEnabled';

export interface SoftphoneTenantDefaultRecordingValueModel {
	recordingEnabled: boolean;
}

/**
 * Tenant-level toggle for the audible "A manager has joined the call"
 * announcement played to the customer the moment a manager **barges**
 * into a live call (Tier 2 coaching). Listen / whisper modes are
 * silent to the customer regardless — the announcement is barge-only
 * because barge is the only mode where the customer can hear the
 * manager.
 *
 * Default: `true`. Some industries (CA two-party consent, healthcare,
 * financial services) effectively require the announcement; others
 * find it disruptive to the customer experience. Tenant admin owns
 * the call.
 *
 * Setting absent = default `true` (announcement plays). Setting
 * present with `enabled: false` = announcement suppressed.
 */
export const SOFTPHONE_TENANT_BARGE_ANNOUNCEMENT_SETTING_KEY =
	'softphone.coachingBargeAnnouncementEnabled';

export interface SoftphoneTenantBargeAnnouncementValueModel {
	enabled: boolean;
}

/**
 * Tenant-level call-recording retention. The recording janitor
 * (`SoftphoneRecordingJanitorService`) runs every 6 hours and
 * deletes recordings older than `retentionDays` from BOTH Twilio's
 * CDN and the local DB columns (`recordingSid`, `recordingUrl`,
 * `recordingDurationSeconds`, `recordingChannels`). The call row
 * itself stays — only the audio + its metadata are purged.
 *
 * Default: setting absent OR `retentionDays: null` → **never
 * delete** (data preservation by default; tenants must explicitly
 * opt in to deletion). Setting `retentionDays: N` deletes
 * recordings whose `_created_date` is more than N days in the
 * past.
 *
 * Compliance note: the policy in effect at JANITOR-RUN time is the
 * one that governs deletion. If a tenant changes retention from 90
 * days to 30 days, the next run will delete every recording older
 * than 30 days — including ones recorded under the prior policy.
 * This is documented behavior; a future iteration may stamp
 * `recordingExpiresAt` at recording-completion time to lock in the
 * policy at recording time.
 */
export const SOFTPHONE_TENANT_RECORDING_RETENTION_SETTING_KEY =
	'softphone.recordingRetentionDays';

export interface SoftphoneTenantRecordingRetentionValueModel {
	/** Days to keep recordings before auto-deletion. `null` = never
	 *  delete (must be explicit; the absence-of-setting also means
	 *  never delete, so the runtime can't tell the difference and
	 *  shouldn't try to). Must be a positive integer. */
	retentionDays: number | null;
}

/**
 * Master Twilio-softphone enabled flag. When `false` (the default),
 * the in-CRM Twilio softphone — voice tokens, call-pop, hold/transfer,
 * coaching, recording, all of it — does NOT activate for the tenant.
 *
 * Workd-user-only: only users with `@workd.com` / `@developrise.com`
 * email addresses can read or set this flag, since enabling Twilio
 * implies billable Twilio usage + provisioned phone numbers under
 * Workd's account. Tenants don't self-serve onto the platform.
 *
 * Default-off semantics: setting absent → flag is off. The admin
 * screen renders the toggle only for Workd users; non-Workd admins
 * never see the option.
 *
 * Disabling for a tenant currently in use does NOT delete numbers,
 * recordings, or audit history — it just gates the runtime so calls
 * stop routing through Twilio. To roll back fully, the Workd team
 * would also need to release the Twilio numbers and clear the
 * tenant's softphone tables (out of scope for the toggle itself).
 */
export const SOFTPHONE_TENANT_TWILIO_ENABLED_SETTING_KEY =
	'softphone.twilioFeatureEnabled';

export interface SoftphoneTenantTwilioEnabledValueModel {
	enabled: boolean;
}

/**
 * Per-tenant override for the phone-diversion behavior. Phone
 * diversion (PhoneDiversionService) routes outbound calls to a test
 * number in non-production environments so dev/staging traffic
 * doesn't hit real customers. Three states:
 *
 *   - Setting absent (or `mode: 'env-default'`) — use environment
 *     default: ON in dev/staging, OFF in production. The vast
 *     majority of tenants stay here.
 *   - `mode: 'force-enabled'` — divert all outbound calls regardless
 *     of environment. Useful for prod-like staging where someone
 *     wants belt-and-suspenders safety.
 *   - `mode: 'force-disabled'` — let outbound calls go through to
 *     real customers regardless of environment. Useful for QA
 *     workflows that need to call a known sandbox phone in staging
 *     without diversion intercepting.
 *
 * Workd-user-only: only `@workd.com` / `@developrise.com` users can
 * read or write this setting via the admin endpoint. Tenants don't
 * self-serve onto disabling diversion in their own staging envs —
 * the failure mode (real customers getting test calls) is too risky
 * to leave to tenant admins who may not understand the implication.
 *
 * The override applies to ALL phone-system integrations (Vonage,
 * RingCentral, Twilio softphone) — diversion is a deployment-level
 * concern, not provider-specific.
 */
export const SOFTPHONE_TENANT_DIVERSION_OVERRIDE_SETTING_KEY =
	'softphone.diversionOverride';

export type SoftphoneTenantDiversionOverrideMode =
	| 'env-default'
	| 'force-enabled'
	| 'force-disabled';

export interface SoftphoneTenantDiversionOverrideValueModel {
	mode: SoftphoneTenantDiversionOverrideMode;
}

/**
 * Fallback options the admin / rep picks for a destination's
 * unanswered behavior.
 *
 *   - `none` (DEFAULT) — straight hangup. Picked as the default
 *     because every other kind needs explicit setup (a greeting, an
 *     agent assignment, a forward target, etc.); defaulting to a
 *     half-configured option would surprise admins.
 *   - `voicemail` — record a message via inline TwiML. The rep's
 *     `voicemailGreetingUrl` plays first when set; otherwise a
 *     default TTS greeting.
 *   - `aiAgent` — hand the call to a `ConversationAgentEntity` for
 *     a live conversation via the existing AI-agent inbound path.
 *   - `forwardToNumber` — `<Dial>` an arbitrary E.164 (mobile, desk
 *     phone, anything outside the system).
 *   - `forwardToUser` — ring another internal user's softphone
 *     clients directly. Does NOT cascade through that user's own
 *     destination chain — "forward to Bob" means "Bob's phone
 *     rings," nothing else.
 *   - `forwardToGroup` — re-enter ring orchestration on another
 *     Group, with the originating user excluded from the candidate
 *     set so the call can't bounce back to them.
 *   - `forwardToRole` — same as group, for an RBAC role
 *     destination.
 *
 * **Per-User vs Group/Role fallback scope.** Forwarding kinds
 * (`forwardToX`) are valid only on per-User configs (a number
 * directly assigned to a single rep). Group / Role configs accept
 * only the non-forwarding kinds — see `SoftphoneGroupRoleFallbackKindModel`
 * below. This mirrors the user-stated rule "only direct rings get
 * forwarded" and keeps the fallback graph shallow: a Group's tail
 * is always voicemail / aiAgent / none, never another forward.
 *
 * Recursion safety. With forwarding User→User chains, the no-answer
 * webhook tracks `fallbackDepth` via TwiML query param. Once depth
 * reaches `SOFTPHONE_MAX_FALLBACK_DEPTH` (2), any further `forwardToX`
 * resolution is downgraded to `none` (hangup). That caps the user
 * journey at 3 destinations (original + 2 forward hops) regardless
 * of how the chain is configured.
 */
export const SOFTPHONE_FALLBACK_KINDS = [
	'none',
	'voicemail',
	'aiAgent',
	'forwardToNumber',
	'forwardToUser',
	'forwardToGroup',
	'forwardToRole',
] as const;

export type SoftphoneFallbackKindModel =
	(typeof SOFTPHONE_FALLBACK_KINDS)[number];

/**
 * Subset of fallback kinds valid on Group / Role configs. Forwarding
 * kinds are intentionally excluded — see `SOFTPHONE_FALLBACK_KINDS`
 * doc for rationale.
 */
export const SOFTPHONE_GROUP_ROLE_FALLBACK_KINDS = [
	'none',
	'voicemail',
	'aiAgent',
] as const;

export type SoftphoneGroupRoleFallbackKindModel =
	(typeof SOFTPHONE_GROUP_ROLE_FALLBACK_KINDS)[number];

export const SOFTPHONE_DEFAULT_FALLBACK_KIND: SoftphoneFallbackKindModel =
	'none';

/**
 * Hard cap on `forwardTo*` hops in a single inbound call. After two
 * forwards the no-answer-fallback handler treats any further
 * `forwardTo*` resolution as `none` (hangup). Tracked via a
 * `fallbackDepth=N` TwiML query param on `<Redirect>` so the cap
 * survives across webhook hits without DB state.
 */
export const SOFTPHONE_MAX_FALLBACK_DEPTH = 2;

/**
 * Shape of `call.metadata` for AI↔human voice handoff (PD-14219). NOT an
 * instance setting — it's per-call state, the voice analog of the SMS
 * `chat_conversation.metadata` AI fields. `VoiceHandoffService` is the single
 * writer; the AI runtime reads it before engaging a call (strict order:
 * `aiPaused` → stop; else `activeAiAgentId` → that agent owns the call).
 *   - `aiPaused`        — true once the call has been handed to a human; the
 *                         AI must not (re)engage.
 *   - `activeAiAgentId` — the ConversationAgent currently owning the call
 *                         (null once handed to a human).
 */
export interface CallAiHandoffStateModel {
	aiPaused?: boolean;
	activeAiAgentId?: number | null;
}
